import "./stylesMonthlyChallengesView.css";

import dayjs from "dayjs";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { CAMPAIGN_API_STATUS } from "../../../../../types/gamePass";
import { useGetCampaignsListByStatuses } from "../../../../../queries/game-pass/useGetCampaignsList";
import { useGetUserEnrollments } from "../../../../../queries/game-pass/useGetUserEnrollments";
import {
  isCampaignEnrollment,
  mapCampaignEnrollmentsToCampaigns,
} from "../../../../../queries/game-pass/userEnrollmentsMappers";
import RebetLoader from "../../../../shared/RebetLoader/RebetLoader";
import CampaignMonthGroup from "./CampaignMonthGroup";

const formatDateRange = (startDate, endDate) => {
  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  if (start?.isValid() && end?.isValid()) {
    return `${start.format("M/D")} - ${end.format("M/D")}`;
  }
  if (start?.isValid()) {
    return start.format("M/D");
  }
  return "";
};

const getCampaignId = (campaign) => campaign?.campaign_id || campaign?.id;

const isPastCampaign = (campaign) =>
  campaign?.status === CAMPAIGN_API_STATUS.COMPLETED ||
  campaign?.status === CAMPAIGN_API_STATUS.CANCELLED;

const campaignStatusBadge = (campaign) => {
  if (
    campaign?.status === CAMPAIGN_API_STATUS.COMPLETED ||
    campaign?.status === CAMPAIGN_API_STATUS.CANCELLED
  ) {
    return { label: AppStrings.COMPLETED, tone: "completed" };
  }
  if (campaign?.status === CAMPAIGN_API_STATUS.DRAFT) {
    return { label: AppStrings.SEASON_STATUS_DRAFT, tone: "in-progress" };
  }
  if (campaign?.status === CAMPAIGN_API_STATUS.SCHEDULED) {
    return { label: AppStrings.SEASON_STATUS_SCHEDULED, tone: "in-progress" };
  }
  return {
    label: AppStrings.USER_GAME_PASS_MONTHLY_STATUS_ACTIVE,
    tone: "active",
  };
};

const enrichWithCatalogue = (enrolledCampaigns, campaigns) => {
  const catalogueById = new Map(
    campaigns.map((campaign) => [getCampaignId(campaign), campaign]),
  );
  return enrolledCampaigns.map((campaign) => {
    const fromCatalogue = catalogueById.get(getCampaignId(campaign));
    if (!fromCatalogue) {
      return campaign;
    }
    return {
      ...fromCatalogue,
      ...campaign,
      name: campaign.name || fromCatalogue.name,
      status: campaign.status || fromCatalogue.status,
      start_date: campaign.start_date || fromCatalogue.start_date,
      end_date: campaign.end_date || fromCatalogue.end_date,
    };
  });
};

/**
 * User Manager → Game Pass → Monthly Challenges (FE-2415 / GP-6).
 * Campaign enrollments (`container_type: CAMPAIGN`) are the source of truth.
 * Season rows stay on the Seasons accordion. The campaigns catalogue is only
 * a fallback when the user has no enrollment ids at all.
 */
const MonthlyChallengesView = ({ userId }) => {
  const campaignsQuery = useGetCampaignsListByStatuses([
    CAMPAIGN_API_STATUS.ACTIVE,
    CAMPAIGN_API_STATUS.COMPLETED,
    CAMPAIGN_API_STATUS.CANCELLED,
  ]);
  const enrollmentsQuery = useGetUserEnrollments(userId, {
    enabled: Boolean(userId),
  });

  const enrollments = enrollmentsQuery.data ?? [];
  const campaigns = campaignsQuery.data ?? [];
  const enrolledCampaigns = mapCampaignEnrollmentsToCampaigns(enrollments);
  const hasCampaignEnrollments = enrolledCampaigns.length > 0;
  const hasEnrollmentContainer = enrollments.some(
    (enrollment) =>
      isCampaignEnrollment(enrollment) ||
      Boolean(enrollment?.season_id?.trim?.()),
  );

  const userCampaigns = hasCampaignEnrollments
    ? enrichWithCatalogue(enrolledCampaigns, campaigns)
    : hasEnrollmentContainer
      ? []
      : campaigns;

  const currentCampaigns = userCampaigns.filter(
    (campaign) => !isPastCampaign(campaign),
  );
  const pastCampaigns = userCampaigns.filter(isPastCampaign);

  if (!userId) {
    return (
      <div
        className="user-manager-game-pass-monthly-view"
        data-testid="game-pass-monthly-view"
      >
        <div
          className="user-manager-game-pass-empty-state"
          data-testid="game-pass-monthly-empty"
        >
          <h3 className="user-manager-game-pass-empty-state-title">
            {AppStrings.GAME_PASS_MONTHLY_EMPTY_TITLE}
          </h3>
          <p className="user-manager-game-pass-empty-state-copy">
            {AppStrings.GAME_PASS_MONTHLY_EMPTY_COPY}
          </p>
        </div>
      </div>
    );
  }

  const isLoading =
    (enrollmentsQuery.isLoading && !hasCampaignEnrollments) ||
    (campaignsQuery.isLoading && !hasCampaignEnrollments);

  if (isLoading) {
    return (
      <div
        className="user-manager-game-pass-monthly-view"
        data-testid="game-pass-monthly-view"
      >
        <div
          className="user-manager-game-pass-monthly-loading"
          data-testid="game-pass-monthly-loading"
        >
          <RebetLoader height="100px" width="100px" />
        </div>
      </div>
    );
  }

  if (campaignsQuery.isError && !hasCampaignEnrollments) {
    return (
      <div
        className="user-manager-game-pass-monthly-view"
        data-testid="game-pass-monthly-view"
      >
        <div
          className="user-manager-game-pass-empty-state"
          data-testid="game-pass-monthly-error"
        >
          <h3 className="user-manager-game-pass-empty-state-title">
            {AppStrings.GAME_PASS_MONTHLY_LOAD_ERROR_TITLE}
          </h3>
          <p className="user-manager-game-pass-empty-state-copy">
            {AppStrings.GAME_PASS_MONTHLY_LOAD_ERROR_COPY}
          </p>
        </div>
      </div>
    );
  }

  if (currentCampaigns.length === 0 && pastCampaigns.length === 0) {
    return (
      <div
        className="user-manager-game-pass-monthly-view"
        data-testid="game-pass-monthly-view"
      >
        <div
          className="user-manager-game-pass-empty-state"
          data-testid="game-pass-monthly-empty"
        >
          <h3 className="user-manager-game-pass-empty-state-title">
            {AppStrings.GAME_PASS_MONTHLY_EMPTY_TITLE}
          </h3>
          <p className="user-manager-game-pass-empty-state-copy">
            {AppStrings.GAME_PASS_MONTHLY_EMPTY_COPY}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="user-manager-game-pass-monthly-view"
      data-testid="game-pass-monthly-view"
    >
      {currentCampaigns.map((campaign, index) => {
        const campaignId = getCampaignId(campaign);
        return (
          <CampaignMonthGroup
            key={campaignId}
            campaign={campaign}
            userId={userId}
            title={campaign.name || AppStrings.MONTHLY_CHALLENGES}
            dateRange={formatDateRange(campaign.start_date, campaign.end_date)}
            status={campaignStatusBadge(campaign)}
            defaultOpen={index === 0}
            readOnly={false}
            testId={`monthly-group-${campaignId}`}
          />
        );
      })}
      {pastCampaigns.map((campaign) => {
        const campaignId = getCampaignId(campaign);
        return (
          <CampaignMonthGroup
            key={campaignId}
            campaign={campaign}
            userId={userId}
            title={campaign.name || AppStrings.MONTHLY_CHALLENGES}
            dateRange={formatDateRange(campaign.start_date, campaign.end_date)}
            status={campaignStatusBadge(campaign)}
            defaultOpen={false}
            readOnly
            testId={`monthly-group-${campaignId}`}
          />
        );
      })}
    </div>
  );
};

export default MonthlyChallengesView;
